'use client';
import React, { useState } from 'react';
import { Plus, Trash2, Edit2, FileText, Clock, Users } from 'lucide-react';
import { toast } from 'sonner';

interface Schedule {
  id: string;
  name: string;
  frequency: string;
  dayOfWeek: string;
  time: string;
  format: 'PDF' | 'CSV';
  recipients: string[];
  sections: string[];
  status: 'active' | 'paused';
}

const initialSchedules: Schedule[] = [
  {
    id: 'sched-001',
    name: 'Executive Weekly Summary',
    frequency: 'Weekly',
    dayOfWeek: 'Monday',
    time: '08:00 AM',
    format: 'PDF',
    recipients: ['alex.mercer@nexusmetrics.io', 'board@nexusmetrics.io', 'cfo@nexusmetrics.io', 'cto@nexusmetrics.io'],
    sections: ['KPI Overview', 'Sales Performance', 'Team Quota'],
    status: 'active',
  },
  {
    id: 'sched-002',
    name: 'Sales Pipeline Export',
    frequency: 'Daily',
    dayOfWeek: 'Every day',
    time: '07:00 AM',
    format: 'CSV',
    recipients: ['priya.nair@nexusmetrics.io', 'sales-team@nexusmetrics.io'],
    sections: ['Product Performance', 'Acquisition Funnel'],
    status: 'active',
  },
  {
    id: 'sched-003',
    name: 'Monthly KPI Board Pack',
    frequency: 'Monthly',
    dayOfWeek: '1st of month',
    time: '09:00 AM',
    format: 'PDF',
    recipients: ['board@nexusmetrics.io', 'investors@nexusmetrics.io'],
    sections: ['All Sections'],
    status: 'active',
  },
  {
    id: 'sched-004',
    name: 'Inventory Risk Report',
    frequency: 'Weekly',
    dayOfWeek: 'Friday',
    time: '05:00 PM',
    format: 'CSV',
    recipients: ['ops@nexusmetrics.io', 'marcus.webb@nexusmetrics.io'],
    sections: ['Inventory Alerts'],
    status: 'paused',
  },
];

export default function ReportScheduleSettings() {
  const [schedules, setSchedules] = useState<Schedule[]>(initialSchedules);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleDelete = (id: string) => {
    setSchedules((prev) => prev.filter((s) => s.id !== id));
    setDeleteId(null);
    toast.success('Report schedule removed');
  };

  const handleToggleStatus = (id: string) => {
    setSchedules((prev) =>
      prev.map((s) =>
        s.id === id ? { ...s, status: s.status === 'active' ? 'paused' : 'active' } : s
      )
    );
    const sched = schedules.find((s) => s.id === id);
    if (sched) {
      toast.success(`"${sched.name}" ${sched.status === 'active' ? 'paused' : 'resumed'}`);
    }
  };

  return (
    <div className="space-y-5">
      <div className="card-surface p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-base font-semibold text-foreground">Report Schedules</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Automated PDF and CSV delivery to your team</p>
          </div>
          <button
            className="flex items-center gap-2 px-3.5 py-2 bg-primary text-primary-foreground text-sm font-semibold rounded-lg hover:bg-primary/90 transition-colors btn-press"
            onClick={() => toast.info('Add schedule modal — connect to form backend')}
          >
            <Plus size={15} />
            Add Schedule
          </button>
        </div>

        <div className="space-y-3">
          {schedules.map((sched) => (
            <div
              key={sched.id}
              className={`rounded-xl border p-4 transition-all duration-150 ${
                sched.status === 'paused' ?'border-border bg-muted/30 opacity-70' :'border-border hover:border-primary/30 hover:bg-primary/5'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    sched.format === 'PDF' ? 'bg-negative/10' : 'bg-positive/10'
                  }`}>
                    <FileText size={15} className={sched.format === 'PDF' ? 'text-negative' : 'text-positive'} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-foreground">{sched.name}</p>
                      <span className={`label-caps rounded-full px-2 py-0.5 ${
                        sched.status === 'active' ? 'bg-positive/10 text-positive' : 'bg-muted text-muted-foreground'
                      }`}>
                        {sched.status}
                      </span>
                      <span className={`label-caps rounded-full px-2 py-0.5 ${
                        sched.format === 'PDF' ? 'bg-negative/10 text-negative' : 'bg-positive/10 text-positive'
                      }`}>
                        {sched.format}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock size={11} />
                        {sched.frequency} · {sched.dayOfWeek} at {sched.time}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Users size={11} />
                        {sched.recipients.length} recipient{sched.recipients.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {sched.sections.map((sec) => (
                        <span key={`sec-${sched.id}-${sec}`} className="text-xs bg-muted text-muted-foreground rounded px-2 py-0.5">
                          {sec}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleToggleStatus(sched.id)}
                    className="px-2.5 py-1.5 text-xs font-medium rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors btn-press"
                  >
                    {sched.status === 'active' ? 'Pause' : 'Resume'}
                  </button>
                  <button
                    onClick={() => toast.info('Edit schedule — connect to form backend')}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors btn-press"
                    aria-label="Edit schedule"
                  >
                    <Edit2 size={13} />
                  </button>
                  {deleteId === sched.id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleDelete(sched.id)}
                        className="px-2 py-1 text-xs font-semibold bg-negative text-white rounded-lg hover:bg-negative/90 transition-colors btn-press"
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => setDeleteId(null)}
                        className="px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteId(sched.id)}
                      className="w-7 h-7 flex items-center justify-center rounded-lg text-muted-foreground hover:text-negative hover:bg-negative/10 transition-colors btn-press"
                      aria-label="Delete schedule — this cannot be undone"
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}